import { createContext, useCallback, useContext, useRef, useState } from "react";
import { useI18n } from "../../i18n";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";

const ConfirmContext = createContext(null);

// confirm({ title, body, confirmLabel, danger, input: { label, minLength } }) -> Promise<false | true | string>
export const ConfirmProvider = ({ children }) => {
  const { t } = useI18n();
  const [opts, setOpts] = useState(null);
  const [value, setValue] = useState("");
  const resolver = useRef(null);

  const confirm = useCallback((o) => new Promise((resolve) => { resolver.current = resolve; setValue(""); setOpts(o); }), []);
  const close = (result) => { resolver.current?.(result); setOpts(null); };
  const tooShort = opts?.input && value.trim().length < (opts.input.minLength ?? 1);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!opts} onOpenChange={(o) => !o && close(false)}>
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{opts?.title}</AlertDialogTitle>
            {opts?.body && <AlertDialogDescription>{opts.body}</AlertDialogDescription>}
          </AlertDialogHeader>
          {opts?.input && (
            <label className="block text-xs text-slate-500">{opts.input.label}
              <textarea rows={3} value={value} onChange={(e) => setValue(e.target.value)} data-testid="confirm-dialog-input" className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-navy outline-none focus:border-brand" />
            </label>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-dialog-cancel" onClick={() => close(false)}>{t("ops.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-dialog-ok" disabled={tooShort} onClick={(e) => { e.preventDefault(); close(opts.input ? value.trim() : true); }}
              className={opts?.danger ? "bg-rose-600 hover:bg-rose-700" : "bg-navy"}>{opts?.confirmLabel || t("ops.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => useContext(ConfirmContext);
